import React, { useRef, useState } from 'react';
import { motion, useScroll, useTransform, useSpring, Variants } from 'framer-motion';
import { Brain, Zap, Shield, Workflow, Cpu, Database, Terminal, Cloud, Mail, Search, Code, Activity, Rocket } from 'lucide-react';

const About: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const workflowRef = useRef<HTMLDivElement>(null);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const { scrollYProgress: workflowProgress } = useScroll({
    target: workflowRef,
    offset: ["start center", "end center"]
  });

  const pathLength = useSpring(workflowProgress, { stiffness: 100, damping: 30 });

  return (
    <div ref={containerRef} className="relative pt-40 pb-20 bg-background overflow-hidden">
      {/* Decorative Background Parallax Elements */}
      <motion.div 
        style={{ y: useTransform(scrollYProgress, [0, 1], [0, -200]) }}
        className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-primary/5 blur-[120px] rounded-full -z-10"
      />
      <motion.div 
        style={{ y: useTransform(scrollYProgress, [0, 1], [0, 200]) }}
        className="absolute bottom-1/4 left-0 w-[400px] h-[400px] bg-accent/5 blur-[120px] rounded-full -z-10"
      />

      <div className="container mx-auto px-6">
        {/* Intro Section */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="max-w-4xl mb-40"
        >
          <div className="inline-flex items-center space-x-2 bg-zinc-900 border border-white/10 px-4 py-1.5 rounded-full mb-8">
            <span className="text-xs font-bold tracking-widest text-accent uppercase">The Duo Behind the Architecture</span>
          </div>
          <h1 className="text-6xl md:text-8xl font-display font-bold mb-10 tracking-tighter leading-[0.9]">
            Architects of <span className="text-accent">Logic</span>. <br />
            Masters of <span className="text-primary">Execution</span>.
          </h1>
          <p className="text-xl md:text-2xl text-zinc-400 leading-relaxed max-w-2xl">
            We craft complete digital solutions—from custom software and mobile applications to cutting-edge frontends and robust backend systems. Two specialized minds, one unified goal: absolute efficiency.
          </p>
        </motion.div>

        {/* The Profiles */}
        <div className="space-y-64 mb-40">
          <ProfileSection 
            id="lionel"
            name="MR LIONEL"
            title="Systems Logic & Architecture Lead"
            email="mrgoldgram@duostacksystems.com"
            desc="The mind behind the 'why' and 'how'. Lionel designs the cognitive layers of our systems—architecting intelligent workflows, software solutions, mobile app logic, and the high-level reasoning that allows applications to make decisions at scale."
            tags={["Software Architecture", "System Design", "Mobile Logic", "Scalability"]}
            icon={<Brain className="w-6 h-6" />}
            alignment="left"
            avatarType="logical"
          />

          <ProfileSection 
            id="maasi"
            name="MAASI"
            title="Full-Stack & Infrastructure Lead"
            email="maasi@duostacksystems.com"
            desc="The force of execution. Maasi translates logic into high-performance reality—building custom software, mobile apps, dynamic frontends, powerful backend systems, automation bots, and ensuring every byte moves with purpose and security."
            tags={["Full-Stack Development", "Mobile Apps", "Backend Systems", "Frontend Excellence"]}
            icon={<Zap className="w-6 h-6" />}
            alignment="right"
            avatarType="foundation"
          />
        </div>

        {/* THE WORKFLOW: TACTICAL EXECUTION PROTOCOL */}
        <section ref={workflowRef} className="mb-64 relative">
          <div className="text-center mb-32">
            <motion.h2 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              className="text-4xl md:text-6xl font-display font-bold mb-6 tracking-tight"
            >
              Tactical <span className="text-accent italic">Execution</span> Protocol
            </motion.h2>
            <p className="text-zinc-500 max-w-xl mx-auto uppercase text-[10px] tracking-[0.5em] font-bold">
              Standard Operating Procedure / v4.2.0
            </p>
          </div>

          <div className="relative max-w-5xl mx-auto">
            {/* The Connecting Logic Line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-[2px] bg-zinc-900 -translate-x-1/2 hidden md:block">
              <svg className="absolute inset-0 w-full h-full">
                <motion.line
                  x1="50%" y1="0" x2="50%" y2="100%"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-accent/30"
                  style={{ pathLength }}
                />
              </svg>
            </div>

            <div className="space-y-32">
              <WorkflowNode 
                step="01"
                title="Discovery & Planning"
                role="Lionel / Architecture"
                desc="Deep-dive into requirements. We define the software architecture, mobile app flow, frontend experience, and backend structure."
                icon={<Search className="w-6 h-6" />}
                type="logic"
                side="left"
              />
              <WorkflowNode 
                step="02"
                title="Development & Build"
                role="Maasi / Execution"
                desc="Building the complete solution—custom software, mobile applications, interactive frontends, and scalable backend APIs."
                icon={<Code className="w-6 h-6" />}
                type="execution"
                side="right"
              />
              <WorkflowNode 
                step="03"
                title="Integration & Testing"
                role="Lionel & Maasi"
                desc="The handoff. Frontend meets backend, mobile syncs with APIs, and all components work in perfect harmony."
                icon={<Activity className="w-6 h-6" />}
                type="dual"
                side="left"
              />
              <WorkflowNode 
                step="04"
                title="Stress Hardening"
                role="Maasi / Security"
                desc="Penetration testing and load balancing audits. We push the system to failure to ensure it never does in production."
                icon={<Shield className="w-6 h-6" />}
                type="execution"
                side="right"
              />
              <WorkflowNode 
                step="05"
                title="Orbital Deployment"
                role="DuoStack / Live"
                desc="Continuous integration triggers. The system is live, monitored 24/7, and scaling dynamically."
                icon={<Rocket className="w-6 h-6" />}
                type="dual"
                side="left"
              />
            </div>
          </div>
        </section>

        {/* Tech Arsenal */}
        <section className="bg-zinc-900/30 border border-white/5 rounded-[40px] p-12 md:p-20 overflow-hidden relative mb-40">
          <div className="absolute top-0 right-0 w-96 h-96 bg-accent/10 blur-[120px] rounded-full" />
          
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h3 className="text-3xl font-display font-bold mb-8">Our Tech Arsenal</h3>
              <p className="text-zinc-400 mb-12">
                We choose the right tool for the job, but we've mastered these frameworks to provide high-velocity results.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <TechBadge icon={<Terminal className="w-4 h-4" />} text="React / Next.js" />
                <TechBadge icon={<Cpu className="w-4 h-4" />} text="React Native / Flutter" />
                <TechBadge icon={<Database className="w-4 h-4" />} text="Node.js / Python" />
                <TechBadge icon={<Cloud className="w-4 h-4" />} text="PostgreSQL / MongoDB" />
                <TechBadge icon={<Terminal className="w-4 h-4" />} text="TypeScript / Django" />
                <TechBadge icon={<Cloud className="w-4 h-4" />} text="AWS / Docker / K8s" />
              </div>
            </div>
            
            <div className="relative aspect-square">
              <div className="absolute inset-0 border-[0.5px] border-white/10 rounded-full animate-pulse-slow" />
              <div className="absolute inset-8 border-[0.5px] border-white/10 rounded-full animate-pulse-slow [animation-delay:2s]" />
              <div className="absolute inset-16 border-[0.5px] border-white/10 rounded-full animate-pulse-slow [animation-delay:4s]" />
              <div className="absolute inset-0 flex items-center justify-center">
                 <span className="text-accent font-display font-bold text-3xl">DUOSTACK</span>
              </div>
            </div>
          </div>
        </section>

        {/* Summary Stats Section */}
        <motion.section 
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="bg-zinc-900/30 border border-white/5 rounded-[48px] p-12 md:p-24 relative overflow-hidden text-center"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-accent/5 to-primary/5 opacity-50" />
          
          <div className="relative z-10 max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-8">The Synergy of Two.</h2>
            <p className="text-zinc-400 text-lg mb-16 leading-relaxed">
              When design meets development, the results are explosive. We don't just 'build'—we engineer complete digital solutions: custom software, mobile apps, beautiful frontends, and robust backends that are resilient by design and intelligent by nature.
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <Stat icon={<Shield className="w-5 h-5 text-accent" />} label="Security" value="Hardened" />
              <Stat icon={<Workflow className="w-5 h-5 text-primary" />} label="Efficiency" value="Optimized" />
              <Stat icon={<Cpu className="w-5 h-5 text-accent" />} label="Uptime" value="99.9%" />
              <Stat icon={<Database className="w-5 h-5 text-primary" />} label="Throughput" value="High-Speed" />
            </div>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

interface WorkflowNodeProps {
  step: string;
  title: string;
  role: string;
  desc: string;
  icon: React.ReactNode;
  type: 'logic' | 'execution' | 'dual';
  side: 'left' | 'right';
}

const WorkflowNode: React.FC<WorkflowNodeProps> = ({ step, title, role, desc, icon, type, side }) => {
  const isLeft = side === 'left';
  const accentColor = type === 'logic' ? 'text-primary' : type === 'execution' ? 'text-accent' : 'text-white';
  const borderColor = type === 'logic' ? 'border-primary/20' : type === 'execution' ? 'border-accent/20' : 'border-white/10';

  return (
    <div className={`flex flex-col md:flex-row items-center justify-center gap-8 md:gap-0 relative z-10`}>
      {/* Node Content */}
      <div className={`w-full md:w-1/2 flex ${isLeft ? 'md:justify-end md:pr-16' : 'md:justify-start md:pl-16 md:order-last'}`}>
        <motion.div 
          initial={{ opacity: 0, x: isLeft ? -30 : 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          whileHover={{ y: -5 }}
          className={`bg-zinc-900/50 border ${borderColor} p-8 rounded-[32px] max-w-sm backdrop-blur-xl relative group`}
        >
          <div className={`text-[10px] font-mono mb-2 uppercase tracking-[0.2em] font-bold ${accentColor}`}>
            Status: Stable // {role}
          </div>
          <h4 className="text-2xl font-display font-bold mb-4">{title}</h4>
          <p className="text-zinc-500 text-sm leading-relaxed">{desc}</p>
          
          {/* Decorative Corner */}
          <div className={`absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-100 transition-opacity ${accentColor}`}>
            {icon}
          </div>
        </motion.div>
      </div>

      {/* Central Node Indicator */}
      <div className="relative z-20">
        <motion.div 
          initial={{ scale: 0 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          className={`w-12 h-12 rounded-full border-2 bg-background flex items-center justify-center shadow-2xl relative ${borderColor} ${accentColor}`}
        >
          <div className="text-[10px] font-bold">{step}</div>
          
          {/* Ripple Effect */}
          <motion.div 
            animate={{ scale: [1, 2], opacity: [0.5, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className={`absolute inset-0 rounded-full border border-current opacity-20`}
          />
        </motion.div>
      </div>

      {/* Spacer for the other side */}
      <div className="hidden md:block md:w-1/2" />
    </div>
  );
};

interface ProfileSectionProps {
  id: string;
  name: string;
  title: string;
  email: string;
  desc: string;
  tags: string[];
  icon: React.ReactNode;
  alignment: 'left' | 'right';
  avatarType: 'logical' | 'foundation';
}

const ProfileSection: React.FC<ProfileSectionProps> = ({ id, name, title, email, desc, tags, icon, alignment, avatarType }) => {
  const isLeft = alignment === 'left';
  const [hovered, setHovered] = useState(false);

  return (
    <div 
      className={`flex flex-col ${isLeft ? 'md:flex-row' : 'md:flex-row-reverse'} items-center gap-16 lg:gap-32 perspective`}
    >
      <motion.div 
        initial={{ opacity: 0, x: isLeft ? -50 : 50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        className="flex-1 w-full max-w-md aspect-[4/5] relative group"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <motion.div 
          animate={{
            rotateX: hovered ? 5 : 0,
            rotateY: hovered ? (isLeft ? 10 : -10) : 0,
            scale: hovered ? 1.05 : 1,
          }}
          transition={{ 
            type: "spring",
            stiffness: 200,
            damping: 20 
          }}
          className="absolute inset-0 bg-zinc-900 border border-white/10 rounded-[40px] overflow-hidden group-hover:border-accent/30 transition-colors duration-500 shadow-2xl"
          style={{ transformStyle: "preserve-3d" }}
        >
          <div className="absolute inset-0 z-20 bg-gradient-to-t from-background via-transparent to-transparent opacity-80" />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ transform: "translateZ(50px)" }}>
            {avatarType === 'logical' ? (
              <svg viewBox="0 0 200 200" className="w-4/5 h-4/5 text-accent opacity-20 group-hover:opacity-60 transition-opacity duration-700">
                <path fill="currentColor" d="M100 20 C60 20 30 50 30 90 C30 110 40 130 55 145 C60 160 70 180 100 180 C130 180 140 160 145 145 C160 130 170 110 170 90 C170 50 140 20 100 20 Z" />
              </svg>
            ) : (
              <svg viewBox="0 0 200 200" className="w-4/5 h-4/5 text-primary opacity-20 group-hover:opacity-60 transition-opacity duration-700">
                <path fill="currentColor" d="M100 25 C70 25 45 50 45 85 C45 120 70 145 70 180 L130 180 C130 145 155 120 155 85 C155 50 130 25 100 25 Z" />
              </svg>
            )}
          </div>
        </motion.div>
        
        <motion.div 
          animate={{ y: [0, -20, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-6 -right-6 p-6 bg-zinc-900 border border-white/10 rounded-3xl backdrop-blur-xl z-30 shadow-xl"
        >
          {icon}
        </motion.div>
      </motion.div>

      <div className="flex-1">
        <h3 className="text-accent font-bold tracking-[0.2em] text-sm uppercase mb-4">{id.toUpperCase()}</h3>
        <h2 className="text-4xl md:text-5xl font-display font-bold mb-2 tracking-tight">{name}</h2>
        <div className="flex flex-col space-y-2 mb-8">
          <p className="text-zinc-500 font-semibold italic">{title}</p>
          <a href={`mailto:${email}`} className="flex items-center space-x-2 text-zinc-400 hover:text-accent transition-colors text-sm font-medium interactive w-fit">
            <Mail className="w-4 h-4" />
            <span>{email}</span>
          </a>
        </div>
        <p className="text-zinc-400 text-lg mb-10 leading-relaxed border-l-2 border-zinc-800 pl-8 transition-colors group-hover:border-accent/50">{desc}</p>
        <div className="flex flex-wrap gap-3">
          {tags.map((tag: string, i: number) => (
            <span key={i} className="text-xs font-bold px-4 py-2 bg-zinc-900 border border-white/5 rounded-full text-zinc-500 hover:text-white hover:border-accent/30 transition-colors duration-300 interactive">{tag}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

const Stat: React.FC<{ icon: React.ReactNode, label: string, value: string }> = ({ icon, label, value }) => (
  <div className="flex flex-col items-center">
    <div className="mb-4">{icon}</div>
    <div className="text-sm font-bold text-white mb-1 uppercase tracking-widest">{value}</div>
    <div className="text-xs text-zinc-500 uppercase tracking-tighter">{label}</div>
  </div>
);

const TechBadge: React.FC<{ icon: React.ReactNode, text: string }> = ({ icon, text }) => (
  <div className="flex items-center space-x-3 bg-zinc-950 border border-white/10 px-6 py-4 rounded-2xl hover:border-accent/50 transition-colors">
    <div className="text-accent">{icon}</div>
    <span className="text-sm font-medium">{text}</span>
  </div>
);

export default About;